// Step 2 — tax pass builder (CAXeng.md §1.1 field→line map, §6.1 proration, §6.2 federal, §6.3 California).
// All Case money is MONTHLY; the tax interfaces are ANNUAL, so every line is ×12 here.
import {
  otherParty,
  type CaTaxInput, type CaTaxResult, type FederalTaxInput, type FederalTaxResult, type InvestmentAndOtherIncome,
  type PartyIndex, type PartyTaxes, type QbidWorksheet, type TaxYear,
} from '@divorceos/schema';
import type { NCase, NExemptions, NParty } from './normalize.js';
import type { TaxProvider } from './provider.js';
import { CAP_LOSS_FLOOR, CAP_LOSS_FLOOR_MFS } from './constants.js';
import { computeQbid } from './qbid.js';

export const ANNUAL = 12;
export const annualToMonthly = (v: number): number => v / ANNUAL;
export const monthlyToAnnual = (v: number): number => v * ANNUAL;

/** TCJA rule: spousal support is federally deductible/taxable only for pre-2019 years or when the setting forces it. */
export const ssFederallyTaxable = (nc: NCase): boolean => nc.year <= 2018 || nc.c.settings.includeSsAfter2018InFederal;
/**
 * Before SB 711 California deducted / taxed spousal support even after TCJA: the SS goes on the CA lines
 * (S+0x1f8 / S+0x1fc) — for every status, MFJIN included. SB 711 (Ch. 231, Stats. 2025) conforms California
 * to the federal rule for orders executed on/after 1 Jan 2026 — then SS is neither deductible nor taxable for
 * CA either, so there is no CA-only SS line and the after-tax cost to the payor equals the benefit to the payee.
 */
export const ssCaOnly = (nc: NCase): boolean => !ssFederallyTaxable(nc) && !nc.c.settings.caConformsAlimony;

/** A block of income lines (a party's own, or the new spouse's), monthly. */
interface IncomeBlock {
  wages: number; pretaxHealth: number; k401: number; selfEmployment: number; other: InvestmentAndOtherIncome;
  /** Health insurance paid by the party (case+0x57c → F+0x68: self-employed health-insurance deduction, limited). */
  healthPaid: number;
  sepSimple: number;
}

const partyBlock = (p: NParty): IncomeBlock => ({
  wages: p.in.wages, pretaxHealth: p.in.healthInsurance.wageDeductionPreTax, k401: p.in.employee401k,
  selfEmployment: p.in.selfEmployment, other: p.in.otherTaxable, healthPaid: p.in.healthInsurance.paidByParty, sepSimple: p.in.adjustments.sepSimple,
});
const newSpouseBlock = (p: NParty): IncomeBlock => ({
  wages: p.in.newSpouseIncome.wages, pretaxHealth: 0, k401: 0, selfEmployment: p.in.newSpouseIncome.selfEmployment, other: p.in.newSpouseIncome,
  healthPaid: 0, sepSimple: 0,
});
const emptyBlock = (): IncomeBlock => ({
  wages: 0, pretaxHealth: 0, k401: 0, selfEmployment: 0, healthPaid: 0, sepSimple: 0,
  other: { capGainShort: 0, capGainLong: 0, rateGain28: 0, unrecaptured1250: 0, qualifiedDividends: 0, nonQualifiedDividends: 0, royalty: 0, rental: 0, interest: 0, otherInvestment: 0, socialSecurity: 0, unemployment: 0, alimonyReceivedFedTaxable: 0, alimonyReceivedFedNonTaxable: 0, partnerAlimonyReceived: 0, other: 0, subtractFromStateAgi: 0, addToStateAgi: 0 },
});

/** Capital gain net of the monthly loss floor (§2.1 FUN_10006123). */
export const capGainFloored = (b: { capGainShort: number; capGainLong: number }, mfs: boolean): number =>
  Math.max(b.capGainShort + b.capGainLong, mfs ? CAP_LOSS_FLOOR_MFS : CAP_LOSS_FLOOR);

/** "Income" used for the joint-return proration ratios (§2.1 FUN_100064ec): wages + SE + other lines + max(capgains,0). */
export function prorationIncome(b: IncomeBlock): number {
  const o = b.other;
  return b.wages + b.selfEmployment + o.qualifiedDividends + o.nonQualifiedDividends + o.interest + o.socialSecurity + o.unemployment
    + o.alimonyReceivedFedTaxable + o.alimonyReceivedFedNonTaxable + o.partnerAlimonyReceived + o.royalty + o.rental + o.other + o.otherInvestment
    + Math.max(b.other.capGainShort + b.other.capGainLong, 0);
}

/** Who else is on party p's return. */
export type JoinMode = 'NONE' | 'OTHER_PARTY' | 'NEW_SPOUSE';
export const joinModeFor = (p: NParty): JoinMode => (p.status === 'MFJIN' ? 'OTHER_PARTY' : p.status === 'MFJOUT' ? 'NEW_SPOUSE' : 'NONE');

export interface PassSpec {
  /** Monthly spousal support paid / received by this party in the scenario. */
  ssPaid: number;
  ssRecv: number;
  exemptions: NExemptions;
  /** For MFJIN joint returns: the other party's exemptions (added to the same form). */
  otherExemptions?: NExemptions;
  /** When set, only this party's own wages/SE are put on the form (used to attribute employment taxes). */
  soloEmployment?: boolean;
}

const sum = (...v: number[]) => v.reduce((a, b) => a + b, 0);

/** Federal input for party p (§6.2 / §1.1 map). */
export function buildFederalInput(nc: NCase, p: NParty, spec: PassSpec): FederalTaxInput {
  const join = spec.soloEmployment ? 'NONE' : joinModeFor(p);
  const other = nc.parties[otherParty(p.index)];
  const own = partyBlock(p);
  const sp = join === 'OTHER_PARTY' ? partyBlock(other) : join === 'NEW_SPOUSE' ? newSpouseBlock(p) : emptyBlock();
  // MFSOUT: the new spouse files separately; only the earned-income facts are passed (spouseWages) — see summary.
  const mfsOutSpouse = p.status === 'MFSOUT' ? newSpouseBlock(p) : null;
  const mfs = p.irs === 'MFS';
  const fedSs = ssFederallyTaxable(nc) && p.status !== 'MFJIN';
  const ex = spec.exemptions;
  const oex = join === 'OTHER_PARTY' ? spec.otherExemptions ?? other.exemptions : null;
  const otherOnly = spec.soloEmployment === true; // employment-tax attribution pass: own income only
  const blocks = [own, sp];
  const line = (f: (b: IncomeBlock) => number) => sum(...blocks.map(f));
  const lineOther = (f: (o: InvestmentAndOtherIncome) => number) => sum(...blocks.map((b) => f(b.other)));
  const taxableWages = line((b) => b.wages - b.pretaxHealth - b.k401);
  const medicareWages = own.wages - own.pretaxHealth; // F+0x2c: the PARTY's FICA/Medicare wages; the spouse's are F+0x30 (spouseWages)
  const earned = (b: IncomeBlock) => b.wages - b.pretaxHealth - b.k401 + b.selfEmployment;
  const alimonyPrior = p.in.ssPaidPrior.thisSpouseFedDeductible + (join === 'NEW_SPOUSE' ? p.in.ssPaidPrior.newSpouseFedDeductible : 0)
    + (join === 'OTHER_PARTY' ? other.in.ssPaidPrior.thisSpouseFedDeductible : 0);
  // Self-employed health-insurance deduction (us_tax FUN_10006392): F+0x68 = health insurance paid (0x57c), allowed up to
  // SE income − retirement-plan contributions; the allowed part moves from Schedule A medical to a Schedule 1 adjustment.
  const healthPaid = otherOnly ? 0 : own.healthPaid + (join === 'OTHER_PARTY' ? other.in.healthInsurance.paidByParty : 0);
  const seHealth = Math.min(healthPaid, Math.max(0, line((b) => b.selfEmployment) - line((b) => b.sepSimple)));
  const A = monthlyToAnnual;
  return {
    year: nc.year as TaxYear,
    status: p.irs,
    wages: A(taxableWages),
    medicareWages: A(medicareWages),
    ssWages: A(medicareWages),
    spouseWages: A(sp.wages - sp.pretaxHealth - sp.k401 + (mfsOutSpouse ? mfsOutSpouse.wages : 0)),
    spouseSelfEmployment: A(sp.selfEmployment + (mfsOutSpouse ? mfsOutSpouse.selfEmployment : 0)),
    taxableInterest: A(lineOther((o) => o.interest)),
    taxExemptInterest: A(otherOnly ? 0 : p.in.nonTaxable.taxExemptInterest + (join === 'OTHER_PARTY' ? other.in.nonTaxable.taxExemptInterest : 0)),
    ordinaryDividends: A(lineOther((o) => o.qualifiedDividends + o.nonQualifiedDividends)),
    qualifiedDividends: A(lineOther((o) => o.qualifiedDividends)),
    capGainShort: A(lineOther((o) => o.capGainShort)),
    capGainLong: A(lineOther((o) => o.capGainLong)),
    rateGain28: A(lineOther((o) => o.rateGain28)),
    unrecaptured1250: A(lineOther((o) => o.unrecaptured1250)),
    selfEmployment: A(line((b) => b.selfEmployment)),
    scheduleE: A(lineOther((o) => o.rental + o.royalty)),
    otherIncome: A(lineOther((o) => o.other + o.otherInvestment)),
    unemployment: A(lineOther((o) => o.unemployment)),
    socialSecurityBenefits: A(lineOther((o) => o.socialSecurity)),
    // F+0x4c = fed-taxable alimony + partner alimony (+ SS received when federally taxable and not MFJIN)
    alimonyReceivedTaxable: A(lineOther((o) => o.alimonyReceivedFedTaxable + o.partnerAlimonyReceived) + (fedSs ? spec.ssRecv : 0)),
    sepSimple: A(p.in.adjustments.sepSimple + (join === 'OTHER_PARTY' ? other.in.adjustments.sepSimple : 0)),
    // F+0x64 = other ATI + qualified mandatory retirement (+ the SE health-insurance deduction). The "charitable" ATI
    // line (case+0x45c, the 2020–21 above-the-line gift) is not mapped to any federal line by FUN_10007aba.
    otherAdjustments: A(p.in.adjustments.otherAti + p.in.mandatoryRetirement.qualifiedAti
      + (join === 'OTHER_PARTY' ? other.in.adjustments.otherAti + other.in.mandatoryRetirement.qualifiedAti : 0)
      + seHealth),
    alimonyPaidDeductible: A(alimonyPrior + (fedSs ? spec.ssPaid : 0)),
    medicalExpenses: A(p.in.healthInsurance.paidByParty + p.in.otherMedical + (join === 'OTHER_PARTY' ? other.in.healthInsurance.paidByParty + other.in.otherMedical : 0) - seHealth),
    realEstateTax: A(p.in.propertyTax + (join === 'OTHER_PARTY' ? other.in.propertyTax : 0)),
    homeMortgageInterest: A(p.in.interestExpense.homeMortgage + (join === 'OTHER_PARTY' ? other.in.interestExpense.homeMortgage : 0)),
    investmentInterest: A(p.in.interestExpense.investmentInterest + (join === 'OTHER_PARTY' ? other.in.interestExpense.investmentInterest : 0)),
    form4952Line4e: A(p.in.interestExpense.form4952Line4e),
    form4952Line4g: A(p.in.interestExpense.form4952Line4g),
    charitable: A(p.in.contributions + (join === 'OTHER_PARTY' ? other.in.contributions : 0)),
    // Miscellaneous itemized deductions (case+0x770 / +0x778) are suspended federally (TCJA) — FUN_10007aba maps them
    // only to the CA Schedule CA line (S+0x204); nothing goes on the federal Schedule A.
    miscDeductions: 0,
    stateSalesTax: A(p.in.miscDeductions.stateSalesTax + (join === 'OTHER_PARTY' ? other.in.miscDeductions.stateSalesTax : 0)),
    forceItemize: mfs && p.in.mfsDeductionMethod === 'ITEMIZE',
    forceStandard: mfs && p.in.mfsDeductionMethod === 'STANDARD',
    qbid: A(p.in.qbid + (join === 'OTHER_PARTY' ? other.in.qbid : 0)),
    dependents: ex.dependent + (oex?.dependent ?? 0),
    qualifyingChildrenCtc: ex.ctc + (oex?.ctc ?? 0),
    qualifyingChildrenEic: ex.eic + (oex?.eic ?? 0),
    qualifyingChildrenCcc: ex.ccc + (oex?.ccc ?? 0),
    otherDependents: ex.otherDependents + (oex?.otherDependents ?? 0),
    age65Count: p.age65 + (join === 'OTHER_PARTY' ? other.age65 : 0),
    blindCount: p.blind + (join === 'OTHER_PARTY' ? other.blind : 0),
    // F+0x640 = care for this relationship + uninsured health + care for other children in the home
    childCareExpenses: A(p.in.childCare.thisRelationship + p.in.childCare.uninsuredHealth + p.in.childCare.otherChildrenInHome
      + (join === 'OTHER_PARTY' ? other.in.childCare.thisRelationship + other.in.childCare.uninsuredHealth + other.in.childCare.otherChildrenInHome : 0)),
    earnedIncome: A(earned(own)),
    spouseEarnedIncome: A(earned(sp) + (mfsOutSpouse ? earned(mfsOutSpouse) : 0)),
    amtHomeMortgageInterest: A(p.in.amt.homeMortgageInterest),
    amtInvestmentInterest: A(p.in.amt.investmentInterest),
    amtDepletion: A(p.in.amt.depletion),
    amtNol: A(p.in.amt.netOperatingLoss),
    amtOtherAdjustments: A(p.in.amt.otherAdjustments),
    mfsLivedTogether: mfs && p.in.taxInfo.mfsLivedTogether,
    mfsQualifiedForEitc: mfs && p.in.taxInfo.mfsQualifiedForEitc,
    otherNonrefundableCredits: A(p.in.nonTaxable.otherNonrefundableCredits + (join === 'OTHER_PARTY' ? other.in.nonTaxable.otherNonrefundableCredits : 0)),
    otherRefundableCredits: A(p.in.nonTaxable.otherRefundableCredits + (join === 'OTHER_PARTY' ? other.in.nonTaxable.otherRefundableCredits : 0)),
    includeSocialSecurityTax: p.in.taxSettings.socialSecurity,
    includeMedicareTax: p.in.taxSettings.medicare,
    includeSeTax: p.in.taxSettings.selfEmploymentTax,
    includeChildCareCredit: p.in.creditOverride.childCareCredit,
    includeEic: p.in.creditOverride.earnedIncomeCredit,
    includeCtc: p.in.creditOverride.childTaxCredit,
    stateIncomeTaxPaid: 0,
    stateSdiPaid: 0,
  };
}

/** California input for party p from the federal pass (§6.3). */
export function buildCaInput(nc: NCase, p: NParty, spec: PassSpec, fedIn: FederalTaxInput, fed: FederalTaxResult): CaTaxInput {
  const join = spec.soloEmployment ? 'NONE' : joinModeFor(p);
  const other = nc.parties[otherParty(p.index)];
  const own = partyBlock(p);
  const sp = join === 'OTHER_PARTY' ? partyBlock(other) : join === 'NEW_SPOUSE' ? newSpouseBlock(p) : emptyBlock();
  const fedSs = ssFederallyTaxable(nc) && p.status !== 'MFJIN';
  const A = monthlyToAnnual;
  const ex = spec.exemptions;
  const oex = join === 'OTHER_PARTY' ? spec.otherExemptions ?? other.exemptions : null;
  const medicalAfterFloor = Math.max(0, fedIn.medicalExpenses - 0.075 * Math.max(0, fed.agi));
  const wages = own.wages - own.pretaxHealth - own.k401;
  const spouseWages = sp.wages - sp.pretaxHealth - sp.k401;
  // Pre-SB711 CA deducts/taxes alimony even after TCJA: the SS not already on the federal return goes on the CA
  // lines (S+0x1f8 / S+0x1fc, FUN_1000904b) — also between MFJIN spouses. ssCaOnly is false once CA conforms (SB 711).
  const ssPaidCaOnly = ssCaOnly(nc) ? spec.ssPaid : 0;
  const ssRecvCaOnly = ssCaOnly(nc) ? spec.ssRecv : 0;
  void fedSs;
  const o = p.in.otherTaxable;
  const investmentIncome = o.interest + p.in.nonTaxable.taxExemptInterest + o.qualifiedDividends + o.nonQualifiedDividends
    + Math.max(0, o.capGainShort + o.capGainLong) + Math.max(0, o.rental + o.royalty);
  return {
    year: nc.year as TaxYear,
    status: p.stateIrs,
    federalAgi: fed.agi,
    federalItemized: {
      medicalAfterFloor,
      realEstateTax: fedIn.realEstateTax,
      mortgageInterest: fedIn.homeMortgageInterest,
      investmentInterest: fedIn.investmentInterest,
      charitable: fedIn.charitable,
      misc: fedIn.miscDeductions,
      stateIncomeTaxDeducted: fedIn.stateIncomeTaxPaid + fedIn.stateSdiPaid,
    },
    federalStandardDeduction: fed.standardDeduction,
    federalItemizing: fed.itemizing,
    federalAdjustments: fed.adjustments,
    federalTaxableSocialSecurity: fed.taxableSocialSecurity,
    unemployment: fedIn.unemployment,
    wages: A(wages),
    spouseWages: A(spouseWages),
    selfEmployment: A(own.selfEmployment),
    spouseSelfEmployment: A(sp.selfEmployment),
    subtractFromAgi: A(o.subtractFromStateAgi + sp.other.subtractFromStateAgi),
    addToAgi: A(o.addToStateAgi + sp.other.addToStateAgi),
    // S+0x1f8 = fed-non-deductible SS paid (+ SS under TCJA); S+0x1f4 = partner SS paid
    alimonyPaidCaDeductible: A(p.in.ssPaidPrior.thisSpouseFedNonDeductible + p.in.ssPaidPrior.partnerByThisSpouse
      + (join === 'NEW_SPOUSE' ? p.in.ssPaidPrior.newSpouseFedNonDeductible + p.in.ssPaidPrior.partnerByNewSpouse : 0)
      + (join === 'OTHER_PARTY' ? other.in.ssPaidPrior.thisSpouseFedNonDeductible + other.in.ssPaidPrior.partnerByThisSpouse : 0)
      + ssPaidCaOnly),
    // S+0x1fc = fed-non-taxable alimony received (+ SS received under TCJA)
    alimonyReceivedCaTaxable: A(o.alimonyReceivedFedNonTaxable + sp.other.alimonyReceivedFedNonTaxable + ssRecvCaOnly),
    unionDues: A(p.in.unionDues + (join === 'OTHER_PARTY' ? other.in.unionDues : 0)),
    jobExpensesAndMisc: A(p.in.miscDeductions.other + p.in.miscDeductions.investmentExpenses + (join === 'OTHER_PARTY' ? other.in.miscDeductions.other + other.in.miscDeductions.investmentExpenses : 0)),
    // S+0x18 = personal + new spouse exemptions (special ones are federal only)
    personalExemptions: ex.personal + ex.newSpouse + (oex ? oex.personal : 0),
    // ca_tax counts the dependent-exemption credit from the federal CTC children + other dependents (US[0x680] + US[0x98]),
    // not from the dependents line (S+0x1c, only used for the phase-out count). They differ only for children at
    // exactly 50 % timeshare, who are qualifying children but not dependents (§2.7).
    dependents: ex.ctc + ex.otherDependents + (oex ? oex.ctc + oex.otherDependents : 0),
    age65Count: fedIn.age65Count,
    blindCount: fedIn.blindCount,
    qualifyingChildrenEic: fedIn.qualifyingChildrenEic,
    hasYoungChild: ex.under6 + (oex?.under6 ?? 0) > 0 && fedIn.qualifyingChildrenEic > 0,
    qualifiesFytc: p.in.taxInfo.qualifiesCaFytc || (join === 'NEW_SPOUSE' && p.in.taxInfo.newSpouseQualifiesCaFytc) || (join === 'OTHER_PARTY' && other.in.taxInfo.qualifiesCaFytc),
    childCareExpenses: fedIn.childCareExpenses,
    earnedIncome: fedIn.earnedIncome,
    spouseEarnedIncome: fedIn.spouseEarnedIncome,
    qualifyingChildrenCcc: fedIn.qualifyingChildrenCcc,
    maxChildTimeshare: p.maxChildTimeshare,
    // No renter flag in the case model: a party paying neither property tax nor mortgage interest is assumed to rent.
    isRenter: p.in.propertyTax === 0 && p.in.interestExpense.homeMortgage === 0,
    isrPenalty: A(p.in.healthInsurance.caIsrPenalty),
    // S+0x434 / S+0x438 = (wages − pre-tax health) × 12 for the party / spouse (401(k) does not reduce SDI wages).
    sdiWages: A(own.wages - own.pretaxHealth + (spec.soloEmployment ? 0 : sp.wages - sp.pretaxHealth)),
    investmentIncome: A(investmentIncome),
    includeStateIncomeTax: p.in.taxSettings.stateIncomeTax,
    includeSdi: p.in.taxSettings.stateDisabilityInsurance,
    includeExemptionCredit: p.in.creditOverride.caExemptionCredit,
    includeJchhCredit: p.in.creditOverride.caJchhCredit,
    includeRentCredit: p.in.creditOverride.caRentCredit,
    includeChildCareCredit: p.in.creditOverride.caChildCareCredit,
    amtHomeMortgageInterest: fedIn.amtHomeMortgageInterest,
    amtInvestmentInterest: fedIn.amtInvestmentInterest,
    amtDepletion: fedIn.amtDepletion,
    amtNol: fedIn.amtNol,
    amtOtherAdjustments: fedIn.amtOtherAdjustments,
  };
}

/** Empty CA result for parties whose tax state is not California (only state code 5 produces a return). */
export function zeroCaResult(): CaTaxResult {
  return {
    caAdjustmentsSubtractions: 0, caAdjustmentsAdditions: 0, caAgi: 0, caDeduction: 0, caItemizing: false, caTaxableIncome: 0,
    taxBeforeCredits: 0, exemptionCredit: 0, jchhCredit: 0, rentCredit: 0, childCareCredit: 0, taxAfterCredits: 0, amt: 0,
    mentalHealthTax: 0, totalTax: 0, caEitc: 0, yctc: 0, fytc: 0, isrPenalty: 0, netIncomeTax: 0, sdi: 0, marginalRate: 0, lines: {},
  };
}

/** One party's (possibly joint) return: two-pass federal/state (§ task step 2). */
export interface ReturnPair { federal: FederalTaxResult; state: CaTaxResult; federalInput: FederalTaxInput }

export function computeReturn(nc: NCase, provider: TaxProvider, p: NParty, spec: PassSpec): ReturnPair {
  const fedIn = buildFederalInput(nc, p, spec);
  const qbidWs = spec.soloEmployment ? null : qbidWorksheetFor(nc, p);
  const federal = (inp: FederalTaxInput): FederalTaxResult => (qbidWs ? federalWithQbidWorksheet(provider, inp, qbidWs) : provider.federal(inp));
  let fed = federal(fedIn);
  let state = zeroCaResult();
  const isCa = nc.c.settings.taxState[p.index] === 5;
  if (isCa) state = provider.state(buildCaInput(nc, p, spec, fedIn, fed));
  const adjusted = spec.soloEmployment ? state : applyStateTaxAdjustment(state, p);
  if (isCa || adjusted !== state) {
    state = adjusted;
    const paid = Math.max(0, state.totalTax);
    if (paid > 0 || state.sdi > 0) {
      // Second federal pass: Schedule A deducts the (adjusted) state income tax + SDI.
      fedIn.stateIncomeTaxPaid = paid;
      fedIn.stateSdiPaid = Math.max(0, state.sdi);
      fedIn.stateTaxableIncome = state.caTaxableIncome;
      fed = federal(fedIn);
    }
  }
  const fedAdjusted = spec.soloEmployment ? fed : applyFederalTaxAdjustment(fed, p);
  return { federal: fedAdjusted, state, federalInput: fedIn };
}

/**
 * PartyInputs.federalTaxAdjustment (optional, TAX-07): userMonthlyAmount replaces the federal income tax with
 * amount × 12; otherwise factorPercent ≠ 100 scales the computed federal net income tax. Carried into netIncomeTax
 * and totalTax, and recorded as lines['federalTaxAdjustment'] (annual delta). Self-employment tax is unchanged.
 * Returns the SAME object when nothing applies. Applied after the federal pass, so it does not feed the CA return.
 */
export function applyFederalTaxAdjustment(fed: FederalTaxResult, p: NParty): FederalTaxResult {
  const adj = p.in.federalTaxAdjustment;
  if (!adj) return fed;
  let target: number;
  if (typeof adj.userMonthlyAmount === 'number' && Number.isFinite(adj.userMonthlyAmount)) target = monthlyToAnnual(adj.userMonthlyAmount);
  else if (typeof adj.factorPercent === 'number' && Number.isFinite(adj.factorPercent) && adj.factorPercent !== 100) target = fed.netIncomeTax * adj.factorPercent / 100;
  else return fed;
  const delta = target - fed.netIncomeTax;
  return {
    ...fed, netIncomeTax: target,
    lines: { ...fed.lines, '1040.net': (fed.lines['1040.net'] ?? 0) + delta, federalTaxAdjustment: delta },
  };
}

/**
 * PartyInputs.stateTaxAdjustment (optional): userMonthlyAmount (a number) replaces the state income tax with
 * amount × 12; otherwise factorPercent ≠ 100 scales the computed CA net income tax. SDI is unchanged. The change is
 * carried into netIncomeTax, totalTax (so the federal Schedule A deduction follows) and lines['540.net'], and
 * recorded as lines['stateTaxAdjustment'] (annual delta). Returns the SAME object when nothing applies. For joint
 * returns the figure is the return's tax (prorated afterwards like the computed one). Skipped when the party's
 * state income tax is switched off.
 */
export function applyStateTaxAdjustment(state: CaTaxResult, p: NParty): CaTaxResult {
  const adj = p.in.stateTaxAdjustment;
  if (!adj || !p.in.taxSettings.stateIncomeTax) return state;
  let target: number;
  if (typeof adj.userMonthlyAmount === 'number' && Number.isFinite(adj.userMonthlyAmount)) target = monthlyToAnnual(adj.userMonthlyAmount);
  else if (typeof adj.factorPercent === 'number' && Number.isFinite(adj.factorPercent) && adj.factorPercent !== 100) target = state.netIncomeTax * adj.factorPercent / 100;
  else return state;
  const delta = target - state.netIncomeTax;
  return {
    ...state, netIncomeTax: target, totalTax: state.totalTax + delta,
    lines: { ...state.lines, '540.net': target, stateTaxAdjustment: delta },
  };
}

/** The QBID worksheet(s) feeding party p's return (both parties' for MFJIN), or null when none is in use. */
export interface ReturnQbidWorksheet {
  worksheet: QbidWorksheet;
  /** Annual QBID entered directly (PartyInputs.qbid × 12) by the parties on the return that do not use a worksheet. */
  plainAnnual: number;
}

export function qbidWorksheetFor(nc: NCase, p: NParty): ReturnQbidWorksheet | null {
  const parties = joinModeFor(p) === 'OTHER_PARTY' ? [p, nc.parties[otherParty(p.index)]] : [p];
  const using = parties.filter((q) => q.in.qbidWorksheet?.use === true);
  if (!using.length) return null;
  const sheets = using.map((q) => q.in.qbidWorksheet as QbidWorksheet);
  return {
    worksheet: {
      use: true,
      businesses: sheets.flatMap((w) => w.businesses ?? []),
      reitPtp: sheets.reduce((a, w) => a + (w.reitPtp || 0), 0),
      lossCarryforward: sheets.reduce((a, w) => a + (w.lossCarryforward || 0), 0),
    },
    plainAnnual: monthlyToAnnual(parties.filter((q) => !using.includes(q)).reduce((a, q) => a + q.in.qbid, 0)),
  };
}

/** Federal pass with QBID from the worksheet: a pass with QBID 0 gives taxable income before QBID and net capital gain. */
export function federalWithQbidWorksheet(provider: TaxProvider, fedIn: FederalTaxInput, ws: ReturnQbidWorksheet): FederalTaxResult {
  fedIn.qbid = 0;
  const base = provider.federal(fedIn);
  const q = computeQbid(ws.worksheet, { taxableIncomeBeforeQbid: base.taxableIncome, netCapitalGain: base.netCapitalGain, status: fedIn.status, year: fedIn.year });
  fedIn.qbid = q.deduction + ws.plainAnnual;
  if (fedIn.qbid === 0) return base;
  const r = provider.federal(fedIn);
  return { ...r, lines: { ...r.lines, qbidWorksheetDeduction: q.deduction } };
}

// ------------------------------------------------------------------------------------------- scenarios

/** Per-party tax outcome of one support scenario (monthly figures, prorated for joint returns). */
export interface TaxOutcome {
  federal: FederalTaxResult;
  state: CaTaxResult;
  /** Monthly prorated income taxes (federal + state): "tax1" of §2.1. */
  incomeTax: number;
  /** Monthly employment taxes attributed to this party (FICA + SE + SDI): "tax2" of §2.1. */
  employmentTax: number;
  taxes: PartyTaxes;
  ratioUs: number;
  ratioSt: number;
  /** New-spouse income net of the prorated taxes attributed to it (out+0xb8), monthly. */
  newMateIncome: number;
}

export interface ScenarioSpec {
  /** Spousal support signed from party 0's point of view: > 0 party 0 pays party 1; < 0 party 1 pays party 0. */
  ss: number;
  /** Form 8332 releases moved from `releaseFrom` to the other party (dependents + CTC children). */
  releases: number;
  releaseFrom: PartyIndex;
  /**
   * Optional per-child releases (ProposedSettings.releaseChildren): of the `releases` children, how many are CTC
   * children (under 17 at year end) and how many are other dependents (17+). When set, those move individually
   * instead of the count-based transfer. Absent = the original count-based behaviour.
   */
  releaseKids?: { ctc: number; odc: number };
  /** Party whose dependent counts are zeroed (dependent exemption value), or null. */
  zeroDependents: PartyIndex | null;
}

export const NO_SUPPORT: ScenarioSpec = { ss: 0, releases: 0, releaseFrom: 1, zeroDependents: null };

/** Party p's exemptions after releases / zeroing. */
export function scenarioExemptions(nc: NCase, p: PartyIndex, spec: ScenarioSpec): NExemptions {
  const base = nc.parties[p].exemptions;
  if (spec.zeroDependents === p) return { ...base, dependent: 0, ctc: 0, eic: 0, ccc: 0, under6: 0, otherDependents: 0 };
  if (spec.releases > 0) {
    // FUN_100079e0: n dependents and n CTC children move from the releasing party to the other, whatever the
    // releasing party's CTC count (its counts are floored at 0 by the tax engines).
    // The "other dependents" (ODC, F+0x98) count is derived at form-fill time as dependents − CTC children (floored),
    // so a released 17+ child stops being anyone's other dependent (verified against the original).
    const n = spec.releases;
    if (spec.releaseKids) {
      const { ctc, odc } = spec.releaseKids;
      return spec.releaseFrom === p
        ? { ...base, dependent: Math.max(0, base.dependent - n), ctc: Math.max(0, base.ctc - ctc), otherDependents: Math.max(0, base.otherDependents - odc) }
        : { ...base, dependent: base.dependent + n, ctc: base.ctc + ctc, otherDependents: base.otherDependents + odc };
    }
    const moved = spec.releaseFrom === p
      ? { ...base, dependent: Math.max(0, base.dependent - n), ctc: Math.max(0, base.ctc - n) }
      : { ...base, dependent: base.dependent + n, ctc: base.ctc + n };
    if (nc.parties[p].in.exemptions.auto) moved.otherDependents = Math.max(0, moved.dependent - Math.max(0, moved.ctc));
    return moved;
  }
  return base;
}

const exKey = (e: NExemptions) => `${e.personal},${e.special},${e.newSpouse},${e.dependent},${e.ctc},${e.eic},${e.ccc},${e.under6},${e.otherDependents}`;

/**
 * Memoising tax context for one normalised case. Every guideline / tactic / cash-flow pass goes through
 * `scenario()`, which is keyed on the facts that can change a return (SS paid/received, exemptions).
 */
export class TaxContext {
  private memo = new Map<string, TaxOutcome>();
  private soloEmployment: (null | { fica: number; se: number; sdi: number })[] = [null, null];
  readonly ratios: [number, number];
  calls = 0;

  constructor(readonly nc: NCase, readonly provider: TaxProvider) {
    this.ratios = [this.ratio(0), this.ratio(1)];
  }

  /**
   * §2.1 proration ratio (FUN_100064ec): MFJIN = party 0's income share (party 1 gets 1 − that), except that when
   * party 1 has no income both ratios are 1 (each party bears the full joint tax — verified against the original)
   * and when only party 0 has no income the ratios are 0 / 1; MFJOUT = 1 − NS/(party+NS); else 1.
   */
  private ratio(p: PartyIndex): number {
    const np = this.nc.parties[p];
    if (np.status === 'MFJIN' && this.nc.prorateJoint) {
      const i0 = prorationIncome(partyBlock(this.nc.parties[0]));
      const i1 = prorationIncome(partyBlock(this.nc.parties[1]));
      if (i1 < 0.1) return 1;
      const r0 = i0 < 0.1 ? 0 : i0 / (i0 + i1);
      return p === 0 ? r0 : 1 - r0;
    }
    if (np.status === 'MFJOUT') {
      const a = prorationIncome(partyBlock(np));
      const ns = prorationIncome(newSpouseBlock(np));
      return a + ns > 0 ? 1 - ns / (a + ns) : 1;
    }
    return 1;
  }

  /** Employment taxes belong to the earner: a solo pass with the party's own wages/SE (joint statuses only). */
  private employmentFor(p: PartyIndex, pair: ReturnPair): { fica: number; se: number; sdi: number } {
    const np = this.nc.parties[p];
    if (joinModeFor(np) === 'NONE') {
      return { fica: pair.federal.ficaSocialSecurity + pair.federal.ficaMedicare, se: pair.federal.selfEmploymentTax, sdi: pair.state.sdi };
    }
    let solo = this.soloEmployment[p];
    if (!solo) {
      const spec: PassSpec = { ssPaid: 0, ssRecv: 0, exemptions: np.exemptions, soloEmployment: true };
      this.calls += 2;
      const r = computeReturn(this.nc, this.provider, np, spec);
      solo = { fica: r.federal.ficaSocialSecurity + r.federal.ficaMedicare, se: r.federal.selfEmploymentTax, sdi: r.state.sdi };
      this.soloEmployment[p] = solo;
    }
    return solo;
  }

  private outcome(p: PartyIndex, pair: ReturnPair): TaxOutcome {
    const np = this.nc.parties[p];
    const rUs = this.ratios[p];
    const rSt = this.ratios[p];
    const emp = this.employmentFor(p, pair);
    const fedIncome = pair.federal.netIncomeTax * rUs;
    const stIncome = pair.state.netIncomeTax * rSt;
    const taxes: PartyTaxes = {
      federalIncome: fedIncome / ANNUAL,
      fica: emp.fica / ANNUAL,
      selfEmployment: emp.se / ANNUAL,
      stateIncome: stIncome / ANNUAL,
      sdi: emp.sdi / ANNUAL,
      total: 0,
    };
    taxes.total = taxes.federalIncome + taxes.fica + taxes.selfEmployment + taxes.stateIncome + taxes.sdi;
    let newMateIncome = 0;
    if (np.status === 'MFJOUT' || np.status === 'MFSOUT') {
      const ns = prorationIncome(newSpouseBlock(np));
      const nsTax = np.status === 'MFJOUT' ? (pair.federal.netIncomeTax + pair.state.netIncomeTax) * (1 - rUs) / ANNUAL : 0;
      newMateIncome = ns - nsTax;
    }
    return { federal: pair.federal, state: pair.state, incomeTax: taxes.federalIncome + taxes.stateIncome, employmentTax: taxes.fica + taxes.selfEmployment + taxes.sdi, taxes, ratioUs: rUs, ratioSt: rSt, newMateIncome };
  }

  /**
   * Taxes of both parties for a support scenario. MFJIN (§6.1, FUN_10006257 without the domestic-partnership
   * proration flag): each party gets its OWN return on the joint income with its own SS paid / received on the CA
   * lines (federal ignores SS between the spouses), and the income tax is then multiplied by the proration ratio.
   */
  scenario(spec: ScenarioSpec): [TaxOutcome, TaxOutcome] {
    const ex0 = scenarioExemptions(this.nc, 0, spec);
    const ex1 = scenarioExemptions(this.nc, 1, spec);
    return [this.party(0, spec.ss > 0 ? spec.ss : 0, spec.ss < 0 ? -spec.ss : 0, ex0, ex1), this.party(1, spec.ss < 0 ? -spec.ss : 0, spec.ss > 0 ? spec.ss : 0, ex1, ex0)];
  }

  private party(p: PartyIndex, paid: number, recv: number, ex: NExemptions, otherEx: NExemptions): TaxOutcome {
    const joint = this.nc.prorateJoint;
    const key = `${p}|${Math.round(paid * 100)}|${Math.round(recv * 100)}|${exKey(ex)}|${joint ? exKey(otherEx) : ''}`;
    let r = this.memo.get(key);
    if (!r) {
      this.calls += 3;
      const pair = computeReturn(this.nc, this.provider, this.nc.parties[p], { ssPaid: paid, ssRecv: recv, exemptions: ex, otherExemptions: joint ? otherEx : undefined });
      r = this.outcome(p, pair);
      this.memo.set(key, r);
    }
    return r;
  }

  /** Combined monthly income tax of both parties for a p0-signed SS (tactic search objective). */
  combinedIncomeTax(spec: ScenarioSpec): number {
    const [a, b] = this.scenario(spec);
    return a.incomeTax + b.incomeTax;
  }
}
